import React from 'react';
import { Dropdown } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { WorkFlowEventOptions } from 'routes/screens/wflow/constants';
import { WorkflowCard } from 'routes/screens/wflow/styles';
import { WorkFlowEventOptionType } from 'routes/screens/wflow/types';
import { useGetSurveys } from 'shared/hooks';
import {
  SurveyType,
  WorkflowConditionVariables,
  WorkflowEventTypes,
  WorkflowType,
} from 'shared/types';

type WorkflowEventProps = {
  id?: string;
  disabled: boolean;
};

const WorkflowEvent = (props: WorkflowEventProps) => {
  const { disabled } = props;
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const event = watch('event');
  const survey_id = watch('survey_id');
  const condition_groups = watch('condition_groups');

  const { data: surveysResponse, isFetching: isFetchingSurveys } =
    useGetSurveys({
      enabled: event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED',
    });

  const workFlowEventOptions = React.useMemo(() => {
    return Object.keys(WorkFlowEventOptions).map((key) => ({
      id: key as WorkflowEventTypes,
      label: t(WorkFlowEventOptions[key]),
    }));
  }, []);

  return (
    <WorkflowCard>
      <div className="wf-title">{`1. ${t('workflow.event.title') || ''}`}</div>
      <div className="wf-row">
        <Dropdown
          placeholder={`${t('forms.placeholders.select')}`}
          isRequired
          label={t('workflow.event.run_workflow') || ''}
          defaultSelectedItem={workFlowEventOptions.find(
            (wfeo) => wfeo.id === event,
          )}
          emptyItemsText={`${t('no_results_found')}`}
          onChange={(wfeo: WorkFlowEventOptionType) => {
            if (event === wfeo.id) return;
            setValue('event', wfeo.id);
            setValue('survey_id', null);
            if (wfeo.id === 'EVERYTIME_A_CONTACT_IS_UNSUBSCRIBED') {
              setValue('condition_groups', null);
              setValue('condition_groups_operator', null);
              setValue('actions', [{}]);
              return;
            }
            if (!condition_groups?.length) {
              setValue('condition_groups', [
                {
                  conditions: [{}],
                  conditions_operator: 'OR',
                },
              ]);
              setValue('condition_groups_operator', 'AND');
            }
            condition_groups?.forEach((cg, cgi) => {
              cg.conditions?.forEach((c, ci) => {
                if (
                  c.variable &&
                  (
                    (wfeo.id === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED'
                      ? ['SURVEY_ID']
                      : wfeo.id === 'EVERYTIME_A_SURVEY_IS_RESPONDED'
                      ? ['QUESTION']
                      : [
                          'QUESTION',
                          'SURVEY_ID',
                        ]) as WorkflowConditionVariables[]
                  ).includes(c.variable)
                ) {
                  setValue(
                    `condition_groups.${cgi}.conditions.${ci}.variable`,
                    null,
                  );
                  setValue(
                    `condition_groups.${cgi}.conditions.${ci}.variable_id`,
                    null,
                  );
                  setValue(
                    `condition_groups.${cgi}.conditions.${ci}.variable_type`,
                    null,
                  );
                  setValue(
                    `condition_groups.${cgi}.conditions.${ci}.operator`,
                    null,
                  );
                  setValue(
                    `condition_groups.${cgi}.conditions.${ci}.value`,
                    null,
                  );
                }
              });
            });
          }}
          width="320px"
          itemToString={(wfeo: WorkFlowEventOptionType) => wfeo.label}
          itemComponent={(wfeo: WorkFlowEventOptionType) => wfeo.label}
          items={workFlowEventOptions}
          disabled={disabled}
          isErrored={submitCount > 0 && !!get(errors, 'event.message')}
          errorText={get(errors, 'event.message', '')}
        />
        {event === 'EVERYTIME_A_SPECIFIC_SURVEY_IS_RESPONDED' && (
          <Dropdown
            placeholder={`${t('forms.placeholders.select')}`}
            label={t('workflow.condition.condition_variable.survey_name') || ''}
            defaultSelectedItem={(surveysResponse?.surveys || []).find(
              (s) => s.id === survey_id,
            )}
            onChange={(s: SurveyType) => {
              setValue('survey_id', s.id, { shouldValidate: true });
            }}
            emptyItemsText={`${t('no_results_found')}`}
            isRequired
            width="320px"
            itemToString={(s: SurveyType) => s.name || ''}
            itemComponent={(s: SurveyType) => s.name || ''}
            items={surveysResponse?.surveys || []}
            disabled={disabled || isFetchingSurveys}
            isErrored={submitCount > 0 && !!get(errors, 'survey_id.message')}
            errorText={get(errors, 'survey_id.message', '')}
          />
        )}
      </div>
    </WorkflowCard>
  );
};

export default WorkflowEvent;
